import styled from 'styled-components';
import React, { useState, useEffect, useContext } from 'react';
import { Formik, Form } from 'formik';
import { Select, MenuItem, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { Link } from 'react-router-dom';
import { Descriptions, Input, Switch, Button as AntButton, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';

import { displayFlex } from '../../styles/Mixins';
import { paragraphReg, paragraphBold } from '../../styles/Typography';
import colors from '../../styles/Colors';
import ContextSnackbar from 'Components/Snackbar';
import Button from 'Components/Button';
import AddButton from 'Components/Button/AddButton';
import { EVENT_PRODUCTS } from 'Queries/SuperAdmin';
import { UPDATE_EVENT_AND_PRODUCT_INFO } from 'Mutations/UpdateEventAndProductInfo';
import { UPDATE_VENUE } from 'Mutations/SuperAdmin/UpdateVenue';
import { SnackbarContext } from 'Store/SnackbarContext';
import { isEmpty } from '../../helpers';
import { subRouteCodes as SUB_ROUTES } from '../../constants/routes';
import { HeadingTwo } from 'Components/Headings';
import { VENUES_COMPLETE } from 'Queries/SuperAdmin/VenuesComplete';
import { timeZones } from '../SuperAdmin/CreateVenue/helpers';

const useStyles = makeStyles({
  underline: {
    '&&&:before': {
      borderBottom: 'none'
    },
    '&&:after': {
      borderBottom: 'none'
    }
  }
});

const initialSelectedOption = {
  id: '',
  name: '',
  description: '',
  startDate: '',
  endDate: ''
};

const initialState = {
  events: [],
  venues: [],
  stallProducts: [],
  rvProducts: [],
  addOnProducts: [],
  productsToEdit: {},
  selectedVenue: initialSelectedOption,
  selectedEvent: initialSelectedOption,
  isEventEdit: false,
  productChanges: {},
  eventName: '',
  eventDesc: '',
  venueEditableFields: {},
  venueChanges: {},
  showVenueDetails: false
};

const getOptions = data => {
  if (!data) return null;

  const options = data.map(data => (
    <MenuItem key={data.id} value={data.id}>
      ID: {data.id} - {data.name}
    </MenuItem>
  ));

  return options;
};

const SuperAdminVenueBase = props => {
  const inputStyleProps = useStyles();
  const { className } = props;
  const [state, setState] = useState(initialState);
  const [messageApi, contextHolder] = message.useMessage();
  const { data: venuesData, refetch: refetchVenues } = useQuery(VENUES_COMPLETE, {
    fetchPolicy: 'network-only'
  });
  const { data: eventProductsData } = useQuery(EVENT_PRODUCTS, {
    variables: { id: +state.selectedEvent.id },
    fetchPolicy: 'network-only'
  });
  const [updateEventAndProductInfo, { loading, error, data: updateResponse }] = useMutation(UPDATE_EVENT_AND_PRODUCT_INFO, {
    refetchQueries: [
      {
        query: VENUES_COMPLETE
      },
      {
        query: EVENT_PRODUCTS,
        variables: { id: +state.selectedEvent.id }
      }
    ]
  });

  const [updateVenue, { loading: updateVenueLoading }] = useMutation(UPDATE_VENUE, {
    refetchQueries: [
      {
        query: VENUES_COMPLETE
      }
    ],
    onCompleted: async data => {
      if (data.updateVenue.error) {
        messageApi.open({
          type: 'error',
          content: `There was an error updating the venue: ${data.updateVenue.error}`
        });
      } else {
        messageApi.open({
          type: 'success',
          content: 'Venue successfully updated'
        });
        setState(prevState => ({
          ...prevState,
          selectedVenue: data.updateVenue.venue,
          venueEditableFields: {},
          venueChanges: {}
        }));

        await refetchVenues();
      }
    },
    onError: error => {
      messageApi.open({
        type: 'error',
        content: `There was an error updating the venue: ${error.message}`
      });
    }
  });

  const { showSnackbar } = useContext(SnackbarContext);

  const handleIsProductEdit = (id, answer) => {
    if (!answer) delete state.productChanges[id];

    setState({
      ...state,
      productsToEdit: {
        ...state.productsToEdit,
        [id]: answer
      }
    });
  };

  const handleProductChange = (e, productKey) => {
    const productKeys = productKey.split('_');

    setState({
      ...state,
      productChanges: {
        ...state.productChanges,
        [productKey]: {
          ...state.productChanges[productKey],
          id: productKeys[1],
          [e.target.name]: e.target.value,
          type: productKeys[0]
        }
      }
    });
  };

  const handleVenueChange = e => {
    const venue = state.venues.filter(venue => venue.id === e.target.value)[0];

    setState({
      ...state,
      selectedVenue: venue,
      events: venue.events.sort((a, b) => +b.id - +a.id),
      selectedEvent: initialSelectedOption,
      stallProducts: [],
      rvProducts: [],
      addOnProducts: [],
      showVenueDetails: true,
      venueEditableFields: {},
      venueChanges: {}
    });
  };

  const handleEventChange = e => {
    const event = state.events.filter(event => event.id === e.target.value)[0];
    setState({
      ...state,
      selectedEvent: event
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!state.eventName && !state.eventDesc && isEmpty(state.productChanges)) return;

    const input = {
      event: {
        id: state.selectedEvent.id,
        name: state.eventName,
        description: state.eventDesc
      },
      products: Object.values(state.productChanges)
    };

    try {
      await updateEventAndProductInfo({
        variables: { input }
      });
    } catch (error) {
      showSnackbar(`Something went wrong ${JSON.stringify(error)}`, {
        error: true,
        duration: 50000
      });
    }
  };

  const handleReset = () => {
    setState({
      ...state,
      eventName: '',
      eventDesc: '',
      isEventEdit: false,
      productsToEdit: {},
      productChanges: {},
      venueEditableFields: {},
      venueChanges: {}
    });
  };

  const toggleVenueEditable = field => {
    setState(prevState => ({
      ...prevState,
      venueEditableFields: {
        ...prevState.venueEditableFields,
        [field]: !prevState.venueEditableFields[field]
      }
    }));
  };

  const handleVenueFieldChange = (field, value, isVenueConfig = false) => {
    setState(prevState => {
      if (isVenueConfig) {
        return {
          ...prevState,
          venueChanges: {
            ...prevState.venueChanges,
            venueConfig: {
              ...(prevState.venueChanges.venueConfig || {}),
              [field]: value
            }
          }
        };
      } else {
        return {
          ...prevState,
          venueChanges: {
            ...prevState.venueChanges,
            [field]: value
          }
        };
      }
    });
  };

  const validateVenueField = (field, value) => {
    // Basic validation
    if (field === 'name' && (!value || value.length < 3)) {
      return 'Venue name must be at least 3 characters';
    }

    if (['feePerProduct', 'percentageFee', 'feeCap', 'minPlatformFee', 'platformFee'].includes(field)) {
      if (value !== undefined && value !== null && value !== '') {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          return 'Must be a valid number';
        }
        if (numValue < 0) {
          return 'Cannot be negative';
        }
      }
    }

    if (['stripeAccountType', 'stripeAccount'].includes(field)) {
      if (!value || value.trim() === '') {
        return `${field === 'stripeAccountType' ? 'Stripe account type' : 'Stripe account'} is required`;
      }
    }

    return null;
  };

  const handleVenueUpdate = () => {
    if (!state.selectedVenue || !state.selectedVenue.id) {
      messageApi.open({
        type: 'warning',
        content: 'Please select a venue first'
      });
      return;
    }

    // Prepare input data
    const input = {
      id: state.selectedVenue.id
    };

    // Add changed fields to input
    Object.keys(state.venueChanges).forEach(field => {
      let value = state.venueChanges[field];

      // Handle venueConfig separately
      if (field === 'venueConfig') {
        input.venueConfig = {};
        Object.keys(value).forEach(configField => {
          input.venueConfig[configField] = Boolean(value[configField]);
        });
        return;
      }

      // Validate field
      const error = validateVenueField(field, value);
      if (error) {
        messageApi.open({
          type: 'error',
          content: error
        });
        throw new Error(error);
      }

      // Convert types
      if (['feePerProduct', 'percentageFee', 'feeCap', 'minPlatformFee', 'platformFee'].includes(field)) {
        value = value !== undefined && value !== null && value !== '' ? Number(value) : null;
      } else if (
        [
          'interactiveMaps',
          'interactiveMapsForRenters',
          'textMessageIndicator',
          'showAssignments',
          'allowCardReaderPayments',
          'requiredMapAssignmentsForRenters',
          'extraChargesEnabled',
          'includeStripeFee',
          'applyPlatformFeeOnZeroDollarOrder'
        ].includes(field)
      ) {
        value = Boolean(value);
      }

      input[field] = value;
    });

    // If no changes, show message and return
    if (Object.keys(input).length <= 1) {
      messageApi.open({
        type: 'warning',
        content: 'No changes to update'
      });
      return;
    }

    // Execute mutation
    try {
      updateVenue({
        variables: { input }
      });
    } catch (error) {
      messageApi.open({
        type: 'error',
        content: `Error updating venue: ${error.message}`
      });
    }
  };

  useEffect(() => {
    if (venuesData)
      setState({
        ...state,
        venues: venuesData.venues.sort((a, b) => +a.id - +b.id)
      });
  }, [venuesData]);

  useEffect(() => {
    if (eventProductsData && eventProductsData.event) {
      setState({
        ...state,
        stallProducts: eventProductsData.event.stallProducts,
        rvProducts: eventProductsData.event.rvProducts,
        addOnProducts: eventProductsData.event.addOnProducts
      });
    }
  }, [eventProductsData]);

  useEffect(() => {
    if (error) {
      showSnackbar(`Something went wrong ${JSON.stringify(error)}`, {
        error: true,
        duration: 50000
      });
    }
  }, [error]);

  useEffect(() => {
    if (loading) {
      showSnackbar(`saving ...`, {
        error: false,
        duration: 500
      });
    }
  }, [loading]);

  useEffect(() => {
    if (updateResponse) {
      try {
        const { success, error } = updateResponse.updateEventAndProductInfo;
        if (success) {
          setState({
            ...state,
            selectedVenue: initialSelectedOption,
            selectedEvent: initialSelectedOption,
            isEventEdit: false,
            productsToEdit: {},
            productChanges: {}
          });
        }
        if (error)
          showSnackbar(error, {
            error: true,
            duration: 50000
          });
      } catch (error) {
        console.log(error);
      }
    }
  }, [updateResponse]);

  return (
    <>
      <div className={className}>
        <ContextSnackbar />
        {contextHolder}
        <div className={`${className}__page-header`}>
          <div className={`${className}__header-wrapper`}>
            <HeadingTwo label="VENUE, EVENTS & PRODUCTS INFO UPDATE" className="" />
          </div>
        </div>
      </div>
      <div className={className}>
        <div className={`${className}__page-body`}>
          <Formik enableReinitialize>
            <Form>
              <div>
                <CreateVenueLink to={SUB_ROUTES.SUPER_ADMIN.CREATE_VENUE}>
                  <AddButton label={'CREATE VENUE'} />
                </CreateVenueLink>
                <h3>choose venue</h3>
                <Select onChange={handleVenueChange} value={state.selectedVenue.id} disabled={state.isEventEdit} disableUnderline>
                  {getOptions(state.venues)}
                </Select>
              </div>

              {state.showVenueDetails && state.selectedVenue.id && (
                <div className={`${className}__page-body-child`}>
                  <h3>Venue Details</h3>
                  <Descriptions bordered column={1} size="small" style={{ marginTop: 10, marginBottom: 20 }}>
                    <Descriptions.Item label="ID">{state.selectedVenue.id}</Descriptions.Item>

                    <Descriptions.Item label="Name">
                      {state.venueEditableFields.name ? (
                        <Input
                          value={state.venueChanges.name !== undefined ? state.venueChanges.name : state.selectedVenue.name}
                          onChange={e => handleVenueFieldChange('name', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('name')}
                          onBlur={() => toggleVenueEditable('name')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.name !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.name}</span>
                          ) : (
                            state.selectedVenue.name
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('name')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Description">
                      {state.venueEditableFields.description ? (
                        <Input
                          value={state.venueChanges.description !== undefined ? state.venueChanges.description : state.selectedVenue.description}
                          onChange={e => handleVenueFieldChange('description', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('description')}
                          onBlur={() => toggleVenueEditable('description')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.description !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.description}</span>
                          ) : (
                            state.selectedVenue.description
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('description')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Phone">
                      {state.venueEditableFields.phone ? (
                        <Input
                          value={state.venueChanges.phone !== undefined ? state.venueChanges.phone : state.selectedVenue.phone}
                          onChange={e => handleVenueFieldChange('phone', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('phone')}
                          onBlur={() => toggleVenueEditable('phone')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.phone !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.phone}</span>
                          ) : (
                            state.selectedVenue.phone
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('phone')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Street">
                      {state.venueEditableFields.street ? (
                        <Input
                          value={state.venueChanges.street !== undefined ? state.venueChanges.street : state.selectedVenue.street}
                          onChange={e => handleVenueFieldChange('street', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('street')}
                          onBlur={() => toggleVenueEditable('street')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.street !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.street}</span>
                          ) : (
                            state.selectedVenue.street
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('street')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Street 2">
                      {state.venueEditableFields.street2 ? (
                        <Input
                          value={state.venueChanges.street2 !== undefined ? state.venueChanges.street2 : state.selectedVenue.street2}
                          onChange={e => handleVenueFieldChange('street2', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('street2')}
                          onBlur={() => toggleVenueEditable('street2')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.street2 !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.street2}</span>
                          ) : (
                            state.selectedVenue.street2
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('street2')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="City">
                      {state.venueEditableFields.city ? (
                        <Input
                          value={state.venueChanges.city !== undefined ? state.venueChanges.city : state.selectedVenue.city}
                          onChange={e => handleVenueFieldChange('city', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('city')}
                          onBlur={() => toggleVenueEditable('city')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.city !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.city}</span>
                          ) : (
                            state.selectedVenue.city
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('city')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="State">
                      {state.venueEditableFields.state ? (
                        <Input
                          value={state.venueChanges.state !== undefined ? state.venueChanges.state : state.selectedVenue.state}
                          onChange={e => handleVenueFieldChange('state', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('state')}
                          onBlur={() => toggleVenueEditable('state')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.state !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.state}</span>
                          ) : (
                            state.selectedVenue.state
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('state')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="ZIP">
                      {state.venueEditableFields.zip ? (
                        <Input
                          value={state.venueChanges.zip !== undefined ? state.venueChanges.zip : state.selectedVenue.zip}
                          onChange={e => handleVenueFieldChange('zip', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('zip')}
                          onBlur={() => toggleVenueEditable('zip')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.zip !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.zip}</span>
                          ) : (
                            state.selectedVenue.zip
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('zip')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Time Zone">
                      {state.venueEditableFields.timeZone ? (
                        <Select
                          value={state.venueChanges.timeZone !== undefined ? state.venueChanges.timeZone : state.selectedVenue.timeZone}
                          onChange={e => handleVenueFieldChange('timeZone', e.target.value)}
                          onBlur={() => toggleVenueEditable('timeZone')}
                          disableUnderline
                          style={{ minWidth: '300px' }}>
                          {timeZones.map(tz => (
                            <MenuItem key={tz.value} value={tz.value}>
                              {tz.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <span>
                          {state.venueChanges.timeZone !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.timeZone}</span>
                          ) : (
                            state.selectedVenue.timeZone
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('timeZone')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Stripe Account">
                      {state.venueEditableFields.stripeAccount ? (
                        <Input
                          value={state.venueChanges.stripeAccount !== undefined ? state.venueChanges.stripeAccount : state.selectedVenue.stripeAccount}
                          onChange={e => handleVenueFieldChange('stripeAccount', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('stripeAccount')}
                          onBlur={() => toggleVenueEditable('stripeAccount')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.stripeAccount !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.stripeAccount}</span>
                          ) : (
                            state.selectedVenue.stripeAccount
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('stripeAccount')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Stripe Account Type">
                      {state.venueEditableFields.stripeAccountType ? (
                        <Select
                          value={
                            state.venueChanges.stripeAccountType !== undefined ? state.venueChanges.stripeAccountType : state.selectedVenue.stripeAccountType
                          }
                          onChange={e => handleVenueFieldChange('stripeAccountType', e.target.value)}
                          onBlur={() => toggleVenueEditable('stripeAccountType')}
                          disableUnderline
                          style={{ minWidth: '300px' }}>
                          <MenuItem value="express">express</MenuItem>
                          <MenuItem value="standard">standard</MenuItem>
                        </Select>
                      ) : (
                        <span>
                          {state.venueChanges.stripeAccountType !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.stripeAccountType}</span>
                          ) : (
                            state.selectedVenue.stripeAccountType
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('stripeAccountType')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Platform Fee">
                      {state.venueEditableFields.platformFee ? (
                        <Input
                          type="number"
                          value={state.venueChanges.platformFee !== undefined ? state.venueChanges.platformFee : state.selectedVenue.platformFee}
                          onChange={e => handleVenueFieldChange('platformFee', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('platformFee')}
                          onBlur={() => toggleVenueEditable('platformFee')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.platformFee !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.platformFee}</span>
                          ) : (
                            state.selectedVenue.platformFee
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('platformFee')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Fee Per Product">
                      {state.venueEditableFields.feePerProduct ? (
                        <Input
                          type="number"
                          value={state.venueChanges.feePerProduct !== undefined ? state.venueChanges.feePerProduct : state.selectedVenue.feePerProduct}
                          onChange={e => handleVenueFieldChange('feePerProduct', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('feePerProduct')}
                          onBlur={() => toggleVenueEditable('feePerProduct')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.feePerProduct !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.feePerProduct}</span>
                          ) : (
                            state.selectedVenue.feePerProduct
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('feePerProduct')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Percentage Fee">
                      {state.venueEditableFields.percentageFee ? (
                        <Input
                          type="number"
                          value={state.venueChanges.percentageFee !== undefined ? state.venueChanges.percentageFee : state.selectedVenue.percentageFee}
                          onChange={e => handleVenueFieldChange('percentageFee', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('percentageFee')}
                          onBlur={() => toggleVenueEditable('percentageFee')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.percentageFee !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.percentageFee}</span>
                          ) : (
                            state.selectedVenue.percentageFee
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('percentageFee')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Fee Cap">
                      {state.venueEditableFields.feeCap ? (
                        <Input
                          type="number"
                          value={state.venueChanges.feeCap !== undefined ? state.venueChanges.feeCap : state.selectedVenue.feeCap}
                          onChange={e => handleVenueFieldChange('feeCap', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('feeCap')}
                          onBlur={() => toggleVenueEditable('feeCap')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.feeCap !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.feeCap}</span>
                          ) : (
                            state.selectedVenue.feeCap
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('feeCap')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Minimum Platform Fee">
                      {state.venueEditableFields.minPlatformFee ? (
                        <Input
                          type="number"
                          value={state.venueChanges.minPlatformFee !== undefined ? state.venueChanges.minPlatformFee : state.selectedVenue.minPlatformFee}
                          onChange={e => handleVenueFieldChange('minPlatformFee', e.target.value)}
                          onPressEnter={() => toggleVenueEditable('minPlatformFee')}
                          onBlur={() => toggleVenueEditable('minPlatformFee')}
                          autoFocus
                        />
                      ) : (
                        <span>
                          {state.venueChanges.minPlatformFee !== undefined ? (
                            <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{state.venueChanges.minPlatformFee}</span>
                          ) : (
                            state.selectedVenue.minPlatformFee
                          )}
                          <EditOutlined onClick={() => toggleVenueEditable('minPlatformFee')} style={{ marginLeft: 8, cursor: 'pointer' }} />
                        </span>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Interactive Maps">
                      <Switch
                        checked={state.venueChanges.interactiveMaps !== undefined ? state.venueChanges.interactiveMaps : state.selectedVenue.interactiveMaps}
                        onChange={checked => handleVenueFieldChange('interactiveMaps', checked)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Interactive Maps For Renters">
                      <Switch
                        checked={
                          state.venueChanges.interactiveMapsForRenters !== undefined
                            ? state.venueChanges.interactiveMapsForRenters
                            : state.selectedVenue.interactiveMapsForRenters
                        }
                        onChange={checked => handleVenueFieldChange('interactiveMapsForRenters', checked)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Text Message Indicator">
                      <Switch
                        checked={
                          state.venueChanges.textMessageIndicator !== undefined
                            ? state.venueChanges.textMessageIndicator
                            : state.selectedVenue.textMessageIndicator
                        }
                        onChange={checked => handleVenueFieldChange('textMessageIndicator', checked)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Show Assignments">
                      <Switch
                        checked={state.venueChanges.showAssignments !== undefined ? state.venueChanges.showAssignments : state.selectedVenue.showAssignments}
                        onChange={checked => handleVenueFieldChange('showAssignments', checked)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Allow Card Reader Payments">
                      <Switch
                        checked={
                          state.venueChanges.allowCardReaderPayments !== undefined
                            ? state.venueChanges.allowCardReaderPayments
                            : state.selectedVenue.allowCardReaderPayments
                        }
                        onChange={checked => handleVenueFieldChange('allowCardReaderPayments', checked)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Required Map Assignments For Renters">
                      <Switch
                        checked={
                          state.venueChanges.requiredMapAssignmentsForRenters !== undefined
                            ? state.venueChanges.requiredMapAssignmentsForRenters
                            : state.selectedVenue.requiredMapAssignmentsForRenters
                        }
                        onChange={checked => handleVenueFieldChange('requiredMapAssignmentsForRenters', checked)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Extra Charges Enabled">
                      <Switch
                        checked={
                          state.venueChanges.extraChargesEnabled !== undefined
                            ? state.venueChanges.extraChargesEnabled
                            : state.selectedVenue.extraChargesEnabled
                        }
                        onChange={checked => handleVenueFieldChange('extraChargesEnabled', checked)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Include Stripe Fee">
                      <Switch
                        checked={state.venueChanges.includeStripeFee !== undefined ? state.venueChanges.includeStripeFee : state.selectedVenue.includeStripeFee}
                        onChange={checked => handleVenueFieldChange('includeStripeFee', checked)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Apply Platform Fee On Zero Dollar Order">
                      <Switch
                        checked={
                          state.venueChanges.applyPlatformFeeOnZeroDollarOrder !== undefined
                            ? state.venueChanges.applyPlatformFeeOnZeroDollarOrder
                            : state.selectedVenue.applyPlatformFeeOnZeroDollarOrder
                        }
                        onChange={checked => handleVenueFieldChange('applyPlatformFeeOnZeroDollarOrder', checked)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Venue Config - Accounting Report">
                      <Switch
                        checked={
                          state.venueChanges.venueConfig?.accountingReport !== undefined
                            ? state.venueChanges.venueConfig.accountingReport
                            : state.selectedVenue.venueConfig?.accountingReport
                        }
                        onChange={checked => handleVenueFieldChange('accountingReport', checked, true)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Venue Config - Recon Report">
                      <Switch
                        checked={
                          state.venueChanges.venueConfig?.reconReport !== undefined
                            ? state.venueChanges.venueConfig.reconReport
                            : state.selectedVenue.venueConfig?.reconReport
                        }
                        onChange={checked => handleVenueFieldChange('reconReport', checked, true)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Venue Config - Required Addons">
                      <Switch
                        checked={
                          state.venueChanges.venueConfig?.requiredAddons !== undefined
                            ? state.venueChanges.venueConfig.requiredAddons
                            : state.selectedVenue.venueConfig?.requiredAddons
                        }
                        onChange={checked => handleVenueFieldChange('requiredAddons', checked, true)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Venue Config - Include Past Events">
                      <Switch
                        checked={
                          state.venueChanges.venueConfig?.includePastEvents !== undefined
                            ? state.venueChanges.venueConfig.includePastEvents
                            : state.selectedVenue.venueConfig?.includePastEvents
                        }
                        onChange={checked => handleVenueFieldChange('includePastEvents', checked, true)}
                      />
                    </Descriptions.Item>

                    <Descriptions.Item label="Venue Config - Deferment Emails Enabled">
                      <Switch
                        checked={
                          state.venueChanges.venueConfig?.defermentEmailsEnabled !== undefined
                            ? state.venueChanges.venueConfig.defermentEmailsEnabled
                            : state.selectedVenue.venueConfig?.defermentEmailsEnabled
                        }
                        onChange={checked => handleVenueFieldChange('defermentEmailsEnabled', checked, true)}
                      />
                    </Descriptions.Item>
                  </Descriptions>

                  <AntButton type="primary" onClick={handleVenueUpdate} loading={updateVenueLoading} style={{ marginBottom: 20 }}>
                    Update Venue
                  </AntButton>
                </div>
              )}

              <div className={`${className}__page-body-child`}>
                {state.selectedVenue.id ? (
                  <div>
                    <h3>Event Name:</h3>
                    {state.isEventEdit ? (
                      <TextField
                        id="eventName"
                        name="name"
                        type="text"
                        defaultValue={state.selectedEvent.name}
                        InputProps={{ classes: inputStyleProps }}
                        onChange={e => setState({ ...state, eventName: e.target.value })}
                      />
                    ) : (
                      <Select onChange={handleEventChange} value={state.selectedEvent.id} disableUnderline>
                        {getOptions(state.events)}
                      </Select>
                    )}

                    {state.selectedEvent.id ? (
                      <>
                        <h3>Event Description:</h3>
                        {state.isEventEdit ? (
                          <TextField
                            id="eventDescription"
                            name="description"
                            type="text"
                            defaultValue={state.selectedEvent.description}
                            InputProps={{ classes: inputStyleProps }}
                            onChange={e => setState({ ...state, eventDesc: e.target.value })}
                          />
                        ) : (
                          <>{state.selectedEvent.description ? state.selectedEvent.description : 'N/A'}</>
                        )}

                        <h3>Dates:</h3>
                        <span>
                          {state.selectedEvent.startDate} - {state.selectedEvent.endDate}
                        </span>
                        <br />
                        {state.isEventEdit ? (
                          <a href="#" onClick={() => setState({ ...state, isEventEdit: false })}>
                            Cancel
                          </a>
                        ) : (
                          <a href="#" onClick={() => setState({ ...state, isEventEdit: true })}>
                            Edit Event
                          </a>
                        )}
                      </>
                    ) : null}
                    <div className={'line'} />
                  </div>
                ) : null}
              </div>

              <div className={`${className}__page-body-child`}>
                {state.selectedEvent.id ? (
                  <FlexWrapper>
                    <FlexRow>
                      <FlexColumn>
                        <div className={`${className}__page-body-child`}>
                          <h3>Stall Products</h3>
                          {state.stallProducts.map(stall => (
                            <div key={stall.id} className={`${className}__page-body-child`}>
                              {state.productsToEdit[`st_${stall.id}`] ? (
                                <>
                                  <TextField
                                    id={`st_name_${stall.id}`}
                                    name="name"
                                    type="text"
                                    defaultValue={stall.name}
                                    InputProps={{ classes: inputStyleProps }}
                                    onChange={e => handleProductChange(e, `st_${stall.id}`)}
                                  />
                                  <TextField
                                    id={`st_description_${stall.id}`}
                                    name="description"
                                    type="text"
                                    defaultValue={stall.description}
                                    InputProps={{ classes: inputStyleProps }}
                                    onChange={e => handleProductChange(e, `st_${stall.id}`)}
                                  />
                                </>
                              ) : (
                                <>
                                  <span> Name: </span>
                                  {stall.name ? stall.name : ''}
                                  <br />
                                  <span> Description: </span>
                                  {stall.description ? stall.description : 'N/A'}
                                </>
                              )}
                              <br />
                              <span> Price: </span> ${stall.price ? stall.price : ''}
                              <br />
                              <span> Dates: </span>
                              {`${stall.startDate} - ${stall.endDate}`}
                              <br />
                              {state.productsToEdit[`st_${stall.id}`] ? (
                                <a href="#" onClick={() => handleIsProductEdit(`st_${stall.id}`, false)}>
                                  Cancel
                                </a>
                              ) : (
                                <a href="#" onClick={() => handleIsProductEdit(`st_${stall.id}`, true)}>
                                  Edit
                                </a>
                              )}
                              <div className={'line'} />
                            </div>
                          ))}
                        </div>
                      </FlexColumn>
                      <FlexColumn>
                        <div className={`${className}__page-body-child`}>
                          <h3>RV Products</h3>
                          {state.rvProducts.map(rv => (
                            <div key={rv.id} className={`${className}__page-body-child`}>
                              {state.productsToEdit[`rv_${rv.id}`] ? (
                                <>
                                  <TextField
                                    id={`rv_name_${rv.id}`}
                                    name="name"
                                    type="text"
                                    defaultValue={rv.name}
                                    InputProps={{ classes: inputStyleProps }}
                                    onChange={e => handleProductChange(e, `rv_${rv.id}`)}
                                  />
                                  <TextField
                                    id={`rv_desc_${rv.id}`}
                                    name="description"
                                    type="text"
                                    defaultValue={rv.description}
                                    InputProps={{ classes: inputStyleProps }}
                                    onChange={e => handleProductChange(e, `rv_${rv.id}`)}
                                  />
                                </>
                              ) : (
                                <>
                                  <span> Name: </span> {rv.name ? rv.name : ''}
                                  <br />
                                  <span> Description: </span>
                                  {rv.description ? rv.description : 'N/A'}
                                </>
                              )}
                              <br />
                              <span> Price: </span> ${rv.price ? rv.price : ''}
                              <br />
                              <span> Dates: </span>
                              {`${rv.startDate} - ${rv.endDate}`}
                              <br />
                              {state.productsToEdit[`rv_${rv.id}`] ? (
                                <a href="#" onClick={() => handleIsProductEdit(`rv_${rv.id}`, false)}>
                                  Cancel
                                </a>
                              ) : (
                                <a href="#" onClick={() => handleIsProductEdit(`rv_${rv.id}`, true)}>
                                  Edit
                                </a>
                              )}
                              <div className={'line'} />
                            </div>
                          ))}
                        </div>
                      </FlexColumn>
                      <FlexColumn>
                        <div className={`${className}__page-body-child`}>
                          <h3>AddOn Products</h3>
                          {state.addOnProducts.map(addOnProduct => (
                            <div key={addOnProduct.addOn.id} className={`${className}__page-body-child`}>
                              {state.productsToEdit[`ao_${addOnProduct.addOn.id}`] ? (
                                <>
                                  <TextField
                                    id={`add_on_name_${addOnProduct.addOn.id}`}
                                    name="name"
                                    type="text"
                                    defaultValue={addOnProduct.addOn.name}
                                    InputProps={{ classes: inputStyleProps }}
                                    onChange={e => handleProductChange(e, `ao_${addOnProduct.addOn.id}`)}
                                  />
                                  <TextField
                                    id={`add_on_desc_${addOnProduct.addOn.id}`}
                                    name="description"
                                    type="text"
                                    defaultValue={addOnProduct.addOn.description}
                                    InputProps={{ classes: inputStyleProps }}
                                    onChange={e => handleProductChange(e, `ao_${addOnProduct.addOn.id}`)}
                                  />
                                </>
                              ) : (
                                <>
                                  <span> Name: </span>
                                  {addOnProduct.addOn.name ? addOnProduct.addOn.name : ''}
                                  <br />
                                  <span> Description: </span>
                                  {addOnProduct.addOn.description ? addOnProduct.addOn.description : 'N/A'}
                                </>
                              )}
                              <br />
                              <span> Price: </span> ${addOnProduct.price ? addOnProduct.price : ''}
                              <br />
                              {state.productsToEdit[`ao_${addOnProduct.addOn.id}`] ? (
                                <a href="#" onClick={() => handleIsProductEdit(`ao_${addOnProduct.addOn.id}`, false)}>
                                  Cancel
                                </a>
                              ) : (
                                <a href="#" onClick={() => handleIsProductEdit(`ao_${addOnProduct.addOn.id}`, true)}>
                                  Edit
                                </a>
                              )}
                              <div className={'line'} />
                            </div>
                          ))}
                        </div>
                      </FlexColumn>
                    </FlexRow>
                  </FlexWrapper>
                ) : null}
              </div>

              <div className={`${className}__page-body-child`}>
                {state.selectedEvent.id ? (
                  <FlexRow>
                    <FlexSmallColumn>
                      <Button type={'submit'} primary onClick={handleSubmit}>
                        Save Changes
                      </Button>
                    </FlexSmallColumn>
                    <FlexSmallColumn>
                      <Button type={'button'} secondary onClick={handleReset}>
                        Reset
                      </Button>
                    </FlexSmallColumn>
                  </FlexRow>
                ) : null}
              </div>
            </Form>
          </Formik>
        </div>
      </div>
    </>
  );
};

const CreateVenueLink = styled(Link)`
  text-decoration: none;
`;

const FlexWrapper = styled.div`
  ${displayFlex}
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  &:first-child {
    margin: 0 0 30px 0;
  }
  @media screen and (max-width: 601px) {
    ${displayFlex}
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    &:first-child {
      margin: 0 0 30px 0;
    }
  }

  div span {
    ${paragraphBold}
    align-self: flex-start;
    margin: 0;
    padding: 20px 0 0;
    font-size: 12px;
  }
`;

const FlexRow = styled.div`
  ${displayFlex}
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
`;

const FlexColumn = styled.div`
  ${displayFlex}
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 400px;
`;

const FlexSmallColumn = styled.div`
  ${displayFlex}
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 180px;
`;

const SuperAdminVenues = styled(SuperAdminVenueBase)`
  &&& {
    ${displayFlex}
    align-items: center;
    width: 100%;
    height: auto;
    padding: 12px 0 11px 20px;
    ${paragraphReg}
    font-size: 14px;
    text-align: left;
    background-color: ${colors.background.primary};
    color: ${colors.text};
    border-radius: 3px;
  }

  &__page-header {
    text-align: left;
    width: 100%
    border-bottom: 1px solid rgb(200, 214, 229);
    &&& {
      margin: 65px 0 25px;
    }

  }

  &__page-body {
    text-align: left;
    width: 100%
    &&& {
      margin: 0;
      padding: 10px
    }

    h3 {
      text-transform: uppercase;
      font-family: IBMPlexSans-Bold;
      font-size: 12px;
      font-weight: 400;
      color: rgb(79, 93, 109);
      margin-top: 0px;
      margin-bottom: 3px;
    }
  }

  &__page-body-child {
    margin-top: 20px;
  }

  &__header-wrapper {
    margin: 20px 0px !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .MuiInputBase-root {
    border: ${colors.text.lightGray2} 1px solid;
    border-radius: 3px;
    padding: 0 10px;
    min-width: 300px;
  }

  .MuiSvgIcon-root {
    width: .8em;
    height: .8em;
  }

  .MuiIconButton-root {
    padding-top: 6px;
  }

  .line {
    background: ${colors.border.primary};
    height: 1px;
    flex: 1;
    position: relative;
    top: 11px;
  }

  /* Fix for Descriptions component layout */
  .ant-descriptions-item-label {
    width: 250px;
    height: 50px !important;
    vertical-align: middle;
  }

  .ant-descriptions-item-content {
    position: relative;
    min-height: 50px;
    display: flex;
    align-items: center;
  }

  .ant-descriptions-item-content span {
    display: flex;
    align-items: center;
    min-height: 32px; /* Match Input height */
  }

  .anticon-edit {
    margin-left: 8px;
  }

  .ant-switch {
    margin-right: 8px;
  }
`;

export default SuperAdminVenues;
